import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { MechanicalPropertyCreate } from '../types';

interface MechanicalPropertyFormProps {
  mechanicalProperties: MechanicalPropertyCreate[];
  onChange: (properties: MechanicalPropertyCreate[]) => void;
  disabled?: boolean;
}

// Vordefinierte mechanische Eigenschaften als Vorschläge
const COMMON_MECHANICAL_PROPERTIES = [
  { name: 'T/S', unit: 'MPa', description: 'Tensile Strength' },
  { name: '0.2 % Y Str', unit: 'MPa', description: '0.2% Yield Strength' },
  { name: '1 % Y Str', unit: 'MPa', description: '1% Yield Strength' },
  { name: 'Elong (A80 %)', unit: '%', description: 'Elongation at 80mm' },
  { name: 'HV', unit: 'HV', description: 'Hardness Vickers' },
  { name: 'HB', unit: 'HB', description: 'Hardness Brinell' },
  { name: 'HRC', unit: 'HRC', description: 'Hardness Rockwell C' },
  { name: 'Charpy Impact', unit: 'J', description: 'Impact Energy' },
  { name: 'Fatigue Strength', unit: 'MPa', description: 'Fatigue Limit' },
];

const MechanicalPropertyForm: React.FC<MechanicalPropertyFormProps> = ({
  mechanicalProperties,
  onChange,
  disabled = false,
}) => {
  const [errors, setErrors] = useState<string[]>([]);

  const validateProperties = (newProperties: MechanicalPropertyCreate[]): string[] => {
    const validationErrors: string[] = [];

    newProperties.forEach((prop, index) => {
      if (!prop.property_name.trim()) {
        validationErrors.push(`Zeile ${index + 1}: Eigenschaftsname ist erforderlich`);
      }

      if (prop.min_value !== undefined && prop.max_value !== undefined) {
        if (prop.min_value >= prop.max_value) {
          validationErrors.push(`Zeile ${index + 1}: Minimum darf nicht größer als Maximum sein`);
        }
      }

      if (prop.min_value !== undefined && prop.min_value < 0) {
        validationErrors.push(`Zeile ${index + 1}: Minimalwert darf nicht negativ sein`);
      }

      if (prop.max_value !== undefined && prop.max_value < 0) {
        validationErrors.push(`Zeile ${index + 1}: Maximalwert darf nicht negativ sein`);
      }
    });

    // Check for duplicate property names
    const propertyNames = newProperties.map((p) => p.property_name.toLowerCase().trim());
    const duplicates = propertyNames.filter((name, index) => propertyNames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      validationErrors.push('Eigenschaften dürfen nicht doppelt vorkommen');
    }

    return validationErrors;
  };

  const handlePropertyChange = (
    index: number,
    field: keyof MechanicalPropertyCreate,
    value: string | number | undefined
  ) => {
    const newProperties = [...mechanicalProperties];

    if (field === 'property_name' || field === 'unit') {
      newProperties[index][field] = value as string;

      // Auto-set unit when property name is selected
      if (field === 'property_name') {
        const commonProperty = COMMON_MECHANICAL_PROPERTIES.find(
          (p) => p.name.toLowerCase() === (value as string).toLowerCase()
        );
        if (commonProperty) {
          newProperties[index].unit = commonProperty.unit;
        }
      }
    } else {
      newProperties[index][field] = value as number | undefined;
    }

    const validationErrors = validateProperties(newProperties);
    setErrors(validationErrors);
    onChange(newProperties);
  };

  const addProperty = () => {
    const newProperty: MechanicalPropertyCreate = {
      property_name: '',
      min_value: undefined,
      max_value: undefined,
      unit: '',
    };
    onChange([...mechanicalProperties, newProperty]);
  };

  const removeProperty = (index: number) => {
    const newProperties = mechanicalProperties.filter((_, i) => i !== index);
    const validationErrors = validateProperties(newProperties);
    setErrors(validationErrors);
    onChange(newProperties);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Mechanische Eigenschaften</Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addProperty}
          disabled={disabled}
          size="small"
        >
          Eigenschaft hinzufügen
        </Button>
      </Box>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {mechanicalProperties.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
          Keine mechanischen Eigenschaften definiert. Klicken Sie auf "Eigenschaft hinzufügen", um
          zu beginnen.
        </Typography>
      ) : (
        <Box>
          {mechanicalProperties.map((property, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      value={property.property_name}
                      onChange={(_, newValue) => {
                        handlePropertyChange(index, 'property_name', newValue || '');
                      }}
                      onInputChange={(_, newInputValue) => {
                        handlePropertyChange(index, 'property_name', newInputValue);
                      }}
                      options={COMMON_MECHANICAL_PROPERTIES.map((p) => p.name)}
                      freeSolo
                      disabled={disabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Eigenschaft"
                          required
                          error={!property.property_name.trim() && mechanicalProperties.length > 0}
                          helperText={
                            !property.property_name.trim() && mechanicalProperties.length > 0
                              ? 'Erforderlich'
                              : ''
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <TextField
                      type="number"
                      label="Min. Wert"
                      value={property.min_value ?? ''}
                      onChange={(e) =>
                        handlePropertyChange(
                          index,
                          'min_value',
                          e.target.value ? parseFloat(e.target.value) : undefined
                        )
                      }
                      inputProps={{ step: 'any' }}
                      disabled={disabled}
                      error={
                        property.min_value !== undefined &&
                        property.max_value !== undefined &&
                        property.min_value >= property.max_value
                      }
                      fullWidth
                      placeholder="optional"
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <TextField
                      type="number"
                      label="Max. Wert"
                      value={property.max_value ?? ''}
                      onChange={(e) =>
                        handlePropertyChange(
                          index,
                          'max_value',
                          e.target.value ? parseFloat(e.target.value) : undefined
                        )
                      }
                      inputProps={{ step: 'any' }}
                      disabled={disabled}
                      error={
                        property.min_value !== undefined &&
                        property.max_value !== undefined &&
                        property.min_value >= property.max_value
                      }
                      fullWidth
                      placeholder="optional"
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Einheit"
                      value={property.unit || ''}
                      onChange={(e) => handlePropertyChange(index, 'unit', e.target.value)}
                      disabled={disabled}
                      fullWidth
                      placeholder="MPa, %, HV"
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <IconButton
                      onClick={() => removeProperty(index)}
                      disabled={disabled}
                      color="error"
                      title="Eigenschaft entfernen"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {mechanicalProperties.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Hinweis:</strong> Min./Max.-Werte sind optional. Lassen Sie Felder leer,
                wenn keine Begrenzung existiert. Bei der Zertifikaterstellung werden gemessene Werte
                gegen diese Bereiche validiert.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MechanicalPropertyForm;
